import type { UserRole } from "@/types/userType";
import { navConfig } from "@/config/navConfig";

const PUBLIC_PATHS = [
  "/",
  "/register",
  "/forgot-password",
  "/verify-code",
  "/reset-password",
  "/setup-account",
  "/set-new-password",
  "/terms",
  "/exam-voucher/action",
  "/addon-invoice/action",
];

export const isPublicPath = (pathname: string): boolean => {
  const normalizedPath = pathname.split("?")[0].split("#")[0];
  return PUBLIC_PATHS.some((publicPath) => {
    if (publicPath === "/") {
      return normalizedPath === "/";
    }
    return normalizedPath.startsWith(publicPath);
  });
};

export const canAccessPath = (
  pathname: string,
  userRole: UserRole | null,
  userPermissions: string[] = []
): boolean => {
  if (!userRole) {
    return isPublicPath(pathname);
  }

  const normalizedPath = pathname.split("?")[0].split("#")[0];

  if (isPublicPath(normalizedPath)) {
    return true;
  }

  if (!normalizedPath.startsWith("/portal")) {
    return false;
  }

  // A path can be reachable from more than one menu entry (e.g. the super-admin
  // "Partners" group and the partner/HR-admin "My Leads" item both point at
  // /portal/partner/leads). Collect EVERY matching entry rather than the first,
  // so access is judged against all routes that actually lead there.
  const matchingNavItems = navConfig.filter((item) => {
    const itemPath = item.href;
    if (normalizedPath.startsWith(itemPath)) {
      if (itemPath === "/portal") {
        return normalizedPath === "/portal";
      }
      return true;
    }

    if (item.activeOnPaths) {
      return item.activeOnPaths.some((activePath) =>
        normalizedPath.startsWith(activePath)
      );
    }

    return false;
  });

  if (matchingNavItems.length === 0) {
    return true;
  }

  const grantsAccess = (item: (typeof matchingNavItems)[number]): boolean => {
    if (item.roles && item.roles.length > 0 && !item.roles.includes(userRole)) {
      return false;
    }
    if (item.requiredPermissions && item.requiredPermissions.length > 0) {
      const hasPermission = item.requiredPermissions.some((perm) =>
        userPermissions.includes(perm)
      );
      if (!hasPermission) {
        return false;
      }
    }

    // Sub-item precision. A grouped entry (e.g. "Access") is reachable by a
    // broader set of roles than some of its children (e.g. the super-admin-only
    // "Users" / "Impersonation Log" pages). When the path targets a specific
    // sub-item, that child's own role/permission gate must also pass — matching
    // exactly what the sidebar renders (useFilteredNav filters sub-items the
    // same way). Paths that only match the group via prefix/activeOnPaths — not
    // a concrete sub-item — remain governed by the group gate above.
    if (item.items && item.items.length > 0) {
      const sub = item.items.find(
        (s) => normalizedPath === s.href || normalizedPath.startsWith(s.href + "/")
      );
      if (sub) {
        if (sub.roles && sub.roles.length > 0 && !sub.roles.includes(userRole)) {
          return false;
        }
        if (sub.requiredPermissions && sub.requiredPermissions.length > 0) {
          const hasSubPermission = sub.requiredPermissions.some((perm) =>
            userPermissions.includes(perm)
          );
          if (!hasSubPermission) {
            return false;
          }
        }
      }
    }

    return true;
  };

  // Accessible if ANY matching entry grants access.
  return matchingNavItems.some(grantsAccess);
};

export const getDefaultPathForRole = (userRole: UserRole | null): string => {
  if (!userRole) {
    return "/portal";
  }

  switch (userRole) {
    case "super_admin":
    case "admin":
    case "business_admin":
      return "/portal";
    case "team_manager":
      return "/portal";
    case "learner":
      return "/portal";
    default:
      return "/portal";
  }
};

// Walks the sidebar config in display order and returns the first concrete,
// navigable path the user is actually allowed to reach. Used as the landing
// target when we can't (or shouldn't) send them to a specific page — e.g. the
// "session active" auto-continue and the Access Denied auto-redirect. Group
// entries (whose own `href` is not a real route) resolve to their first
// accessible sub-item. Falls back to the role default (`/portal`), which every
// role can reach.
export const getFirstAccessiblePath = (
  userRole: UserRole | null,
  userPermissions: string[] = []
): string => {
  const gateAllows = (
    roles?: UserRole[],
    requiredPermissions?: string[]
  ): boolean => {
    if (roles && roles.length > 0 && (!userRole || !roles.includes(userRole))) {
      return false;
    }
    if (requiredPermissions && requiredPermissions.length > 0) {
      const hasPermission = requiredPermissions.some((perm) =>
        userPermissions.includes(perm)
      );
      if (!hasPermission) return false;
    }
    return true;
  };

  for (const item of navConfig) {
    if (item.hidden) continue;
    if (!gateAllows(item.roles, item.requiredPermissions)) continue;

    // Grouped entry: its own href isn't a route, so pick the first sub-item the
    // user can reach (sub-items may narrow the group's roles/permissions).
    if (item.items && item.items.length > 0) {
      const sub = item.items.find((s) =>
        !s.hidden &&
        gateAllows(s.roles ?? item.roles, s.requiredPermissions ?? item.requiredPermissions)
      );
      if (sub) return sub.href;
      continue;
    }

    return item.href;
  }

  return getDefaultPathForRole(userRole);
};